/**
 * The Pro page's body blocks in the Portal vocabulary (design_handoff_pro_page/README.md
 * "Blocks by version / tab"): borderless white panels on #F5F5F5, 12px/700/.16em headings, pill
 * controls, round avatars, `#F2F2F2` chips and rules. Data shapes are the existing ProBusinessDto's;
 * only the presentation is new. Every block takes `phone` for the 22/18 radius and padding.
 */
import React, { useEffect, useState } from 'react';
import { webOrigin } from '../../utils/brand';
import { bestPercent } from '../../utils/proSessionPricing';
import { Link } from 'react-router-dom';
import BrandedQR from '../BrandedQR';
import { AddressMap } from './vendor/PlaceInfo';
import { DAYS, DAY_ORDER, hoursRowText, openState, attrLabel, clock12, money, monthDay, dayMonthDay, daysLabel, weeklyLabel, durationLabel, priceLabel } from './vendor/vendorUi';
import { zoneLabel, viewerZone } from '../AvailabilityView';
import { HOT_PINK, MUTED, FAINT } from './proPageModel';

const CHIP = '#F2F2F2';
const RULE = '2px solid #F2F2F2';
export const head = (size = 12, spacing = '0.16em') => ({ fontSize: size, fontWeight: 700, letterSpacing: spacing, textTransform: 'uppercase' });

/* ── The panel ─────────────────────────────────────────────────────────────── */
export function Block({ title, right, rightPill, rightLink, onRightLink, black = false, phone = false, children, testId, id, style, gap = 14 }) {
  return (
    <section id={id} className="flex min-w-0 flex-col font-mono" data-testid={testId}
      style={{ background: black ? '#000' : '#fff', color: black ? '#fff' : '#000', borderRadius: phone ? 22 : 24, padding: phone ? '18px 16px' : '22px 24px', gap, ...style }}>
      {title && (
        <span className="flex flex-wrap items-center justify-between" style={{ gap: 8 }}>
          <span style={head()}>{title}</span>
          {right && <span style={{ ...head(9, '0.14em'), color: FAINT }}>{right}</span>}
          {rightPill && <span className="rounded-full" style={{ padding: '5px 10px', background: rightPill.bg, color: rightPill.fg, ...head(9, '0.14em') }} data-testid={rightPill.testId}>{rightPill.label}</span>}
          {rightLink && <button type="button" onClick={onRightLink} className="font-mono" style={{ ...head(10, '0.14em'), background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }}>{rightLink}</button>}
        </span>
      )}
      {children}
    </section>
  );
}

export function EmptyBlock({ title, right, emptyTitle, body, cta, onCta, ctaTo, ctaTestId, minH = 160, phone = false, testId, bodyTestId }) {
  const btn = { marginTop: 6, height: 36, padding: '0 16px', border: 'none', borderRadius: 999, background: '#000', color: '#fff', fontFamily: 'inherit', ...head(10, '0.14em'), cursor: 'pointer', display: 'inline-flex', alignItems: 'center', textDecoration: 'none' };
  return (
    <Block title={title} right={right} phone={phone} testId={testId}>
      <div className="flex flex-col items-center justify-center text-center" style={{ gap: 8, minHeight: minH, padding: 24, borderRadius: 20, background: '#F5F5F5' }} data-testid={bodyTestId}>
        <span style={{ ...head(11), color: FAINT }}>{emptyTitle}</span>
        {body && <span style={{ maxWidth: 360, fontSize: 12, lineHeight: 1.6, color: MUTED }}>{body}</span>}
        {cta && (ctaTo ? <Link to={ctaTo} style={btn} data-testid={ctaTestId}>{cta}</Link> : <button type="button" onClick={onCta} style={btn} data-testid={ctaTestId}>{cta}</button>)}
      </div>
    </Block>
  );
}

/* ── Pills ─────────────────────────────────────────────────────────────────── */
export function ActionPill({ children, fill = true, onClick, href, disabled, small = false, testId, ...rest }) {
  const style = { flexShrink: 0, height: small ? 34 : 36, padding: '0 16px', border: 'none', borderRadius: 999, background: fill ? '#000' : CHIP, color: fill ? '#fff' : '#000', fontFamily: 'inherit', ...head(10, '0.14em'), fontWeight: fill ? 800 : 700, cursor: disabled ? 'default' : 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', opacity: disabled ? 0.5 : 1, transition: 'background-color .2s, color .2s' };
  const cls = 'font-mono hover:bg-[#FF00AA] hover:text-white';
  if (href) return <a href={href} target="_blank" rel="noreferrer" onClick={onClick} className={cls} style={style} data-testid={testId} {...rest}>{children}</a>;
  return <button type="button" onClick={onClick} disabled={disabled} className={cls} style={style} data-testid={testId} {...rest}>{children}</button>;
}

/** A page-level CTA: `flex: 1 1 0`, 54px (48 on phones), black primary / white secondary with the 2px inset ring. */
export function CtaPill({ children, primary = false, onClick, href, tel, disabled, phone = false, fontSize, testId, ...rest }) {
  const style = { flex: '1 1 0', minWidth: 0, height: phone ? 48 : 54, padding: '0 14px', border: 'none', borderRadius: 999, background: primary ? '#000' : '#fff', color: primary ? '#fff' : '#000', boxShadow: primary ? 'none' : 'inset 0 0 0 2px #000', fontFamily: 'inherit', fontSize: fontSize || (phone ? 10 : 12), fontWeight: 800, letterSpacing: '0.12em', lineHeight: 1.25, textWrap: 'balance', textTransform: 'uppercase', cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', textDecoration: 'none', opacity: disabled ? 0.5 : 1, transition: 'background-color .2s, color .2s' };
  const cls = 'font-mono hover:bg-[#FF00AA] hover:text-white';
  if (tel) return <a href={`tel:${tel}`} onClick={onClick} className={cls} style={style} data-testid={testId} {...rest}>{children}</a>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" onClick={onClick} className={cls} style={style} data-testid={testId} {...rest}>{children}</a>;
  return <button type="button" onClick={onClick} disabled={disabled} className={cls} style={style} data-testid={testId} {...rest}>{children}</button>;
}

/* ── ABOUT ─────────────────────────────────────────────────────────────────── */
export function AboutBlock({ biz, phone, onOutbound }) {
  const listed = !!biz.isListed;
  const site = biz.websiteUrl || biz.listedWebsite;
  const insta = listed && biz.listedInstagram ? (biz.listedInstagram.startsWith('http') ? biz.listedInstagram : `https://instagram.com/${biz.listedInstagram.replace(/^@/, '')}`) : null;
  const norm = (u) => String(u || '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '').toLowerCase();
  const links = [site ? { label: 'WEBSITE ↗', href: site } : null, insta ? { label: 'INSTAGRAM ↗', href: insta } : null, ...(biz.links || []).map((l) => ({ label: `${(l.label || 'LINK').toUpperCase()} ↗`, href: l.url }))]
    .filter(Boolean).filter((l, i, all) => all.findIndex((o) => norm(o.href) === norm(l.href)) === i);
  const city = biz.baseCity || biz.listedCity;
  if (!biz.bio && links.length === 0) return null;
  return (
    <Block title="ABOUT" right={listed ? 'FROM THEIR SITE' : undefined} phone={phone} testId="about-block">
      {biz.bio ? <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, textWrap: 'pretty' }}>{biz.bio}</p> : <p style={{ margin: 0, fontSize: 13, color: MUTED }}>Nothing written yet.</p>}
      {links.length > 0 && (
        <span className="flex flex-wrap" style={{ gap: 6 }}>
          {links.map((l) => <ActionPill key={l.label + l.href} fill={false} href={l.href} onClick={() => onOutbound?.('website')} small>{l.label}</ActionPill>)}
        </span>
      )}
      {listed && city && <span className="uppercase" style={{ fontSize: 11, color: MUTED }}>{city}</span>}
    </Block>
  );
}

/* ── Rows (services, snacks, shop services) ────────────────────────────────── */
export function Row({ img, name, sub, tag, price, note, action, first = false }) {
  return (
    <div className="flex items-center" style={{ gap: 14, padding: '12px 0', borderTop: first ? 'none' : RULE }} data-testid="pro-row">
      {img && <img src={img} alt="" className="block flex-none object-cover" style={{ width: 52, height: 52, borderRadius: 14 }} />}
      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{name}</span>
        {sub && <span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>{sub}</span>}
        {tag && <span style={{ ...head(8, '0.14em'), color: MUTED }}>{tag}</span>}
      </span>
      <span className="flex flex-none flex-col items-end" style={{ gap: 3 }}>
        <span style={{ fontSize: 14, fontWeight: 800 }}>{price}</span>
        {note && <span style={{ ...head(8, '0.14em'), color: FAINT }}>{note}</span>}
      </span>
      {action}
    </div>
  );
}

const stripAsPosted = (s) => (s || '').replace(/\s*\(as posted\)\s*$/i, '');
/** "UP TO 4 SESSIONS · SAVE UP TO 5%" on a service that takes several up front. */
export const packageLine = (s) => {
  if (!(s.maxSessions >= 2) || !s.isBookable) return null;
  const best = bestPercent(s.sessionDiscounts);
  return `UP TO ${s.maxSessions} SESSIONS${best ? ` · SAVE UP TO ${best}%` : ''}`;
};
export const servicePrice = (s) => (s.priceCents > 0 ? `$${(s.priceCents / 100).toFixed(0)}` : stripAsPosted(s.priceTerms) || 'Ask');

export function ServicesBlock({ biz, phone, onBook, onOffer }) {
  const listed = !!biz.isListed;
  const mine = !!biz.isMine;
  const services = biz.services || [];
  return (
    <Block title="SERVICES" right={listed ? 'PRICES AS POSTED · NOT BOOKABLE YET' : undefined} phone={phone} id="pro-services" testId="services-block">
      <div className="flex flex-col">
        {services.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>{listed ? 'No prices posted publicly. Ask them in your request.' : 'No set services — make an offer below.'}</span>}
        {services.map((s, i) => (
          <Row key={s.id} first={i === 0} img={listed ? null : s.photoUrl} name={s.descriptionText}
            sub={[s.note, s.durationMinutes > 0 ? `${s.durationMinutes} min` : null, !listed ? s.priceTerms : null, !listed ? packageLine(s) : null].filter(Boolean).join(' · ')}
            price={servicePrice(s)} note={listed ? 'AS POSTED' : null}
            action={!listed && !mine && s.isBookable && s.durationMinutes > 0 ? <ActionPill onClick={() => onBook?.(s)}>BOOK</ActionPill> : null} />
        ))}
      </div>
      {!listed && !mine && (
        <button type="button" onClick={onOffer} className="flex items-center justify-between rounded-full font-mono" style={{ gap: 12, height: 48, padding: '0 20px', border: 'none', background: '#F5F5F5', cursor: 'pointer' }} data-testid="make-offer-foot">
          <span style={{ fontSize: 12 }}>Something else?</span>
          <span style={{ ...head(10, '0.14em'), fontWeight: 800, color: HOT_PINK }}>MAKE ME AN OFFER →</span>
        </button>
      )}
    </Block>
  );
}

export function ShopServicesBlock({ biz, phone, onBook }) {
  const listed = !!biz.isListed;
  const mine = !!biz.isMine;
  const rows = (biz.menuSections || []).flatMap((s) => s.items || []);
  return (
    <Block title="SERVICES" right={listed ? 'PRICES AS POSTED · NOT BOOKABLE YET' : 'FROM-PRICES · FINAL QUOTE AFTER WE LOOK'} phone={phone} testId="shop-services">
      <div className="flex flex-col">
        {rows.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>No services listed yet.</span>}
        {rows.map((it, i) => {
          const book = it.bookMode === 'book';
          return (
            <Row key={it.id || i} first={i === 0} name={it.name} sub={[it.description, durationLabel(it.durationMinutes)].filter(Boolean).join(' · ')}
              tag={!listed && it.bookMode && it.bookMode !== 'none' ? (book ? 'BOOKABLE' : 'QUOTE') : null}
              price={it.priceCents ? `FROM ${money(it.priceCents)}` : (priceLabel(it) || 'ASK').toUpperCase()} note={listed ? 'AS POSTED' : null}
              action={!listed && !mine && it.bookMode && it.bookMode !== 'none' ? <ActionPill fill={book} onClick={() => onBook?.(it)}>{book ? 'BOOK' : 'QUOTE'}</ActionPill> : null} />
          );
        })}
      </div>
    </Block>
  );
}

export function SnacksBlock({ sections, phone }) {
  const items = (sections || []).flatMap((s) => s.items || []);
  if (items.length === 0) return null;
  return (
    <Block title="SNACKS" phone={phone} testId="snacks-block">
      <div className="flex flex-col">{items.map((it, i) => <Row key={it.id || i} first={i === 0} name={it.name} sub={it.description} price={priceLabel(it)} />)}</div>
    </Block>
  );
}

/* ── KNOWN FOR tiles ───────────────────────────────────────────────────────── */
export function KnownForBlock({ topThings, phone, linkLabel, onLink, onTap }) {
  const items = (topThings || []).slice(0, phone ? 4 : 6);
  if (items.length === 0) return null;
  return (
    <Block title="WHAT THEY’RE KNOWN FOR" rightLink={linkLabel} onRightLink={onLink} phone={phone} testId="short-menu">
      <div className="grid" style={{ gridTemplateColumns: `repeat(${phone ? 2 : 3}, minmax(0, 1fr))`, gap: 12 }}>
        {items.map((t) => (
          <button key={t.id} type="button" onClick={() => onTap?.(t)} className="flex min-w-0 flex-col text-left font-mono" style={{ gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }} data-testid="top-thing">
            <span className="block w-full overflow-hidden" style={{ aspectRatio: '4 / 3', borderRadius: 18, background: '#F2F2F2' }}>{t.photoUrl && <img src={t.photoUrl} alt={t.name} className="block h-full w-full object-cover" />}</span>
            <span className="flex justify-between" style={{ gap: 10, padding: '0 4px' }}><span style={{ fontSize: 12, lineHeight: 1.4 }}>{t.name}</span><span className="flex-none" style={{ fontSize: 12, fontWeight: 800 }}>{priceLabel(t)}</span></span>
          </button>
        ))}
      </div>
    </Block>
  );
}

/* ── Photo grid (WORK / PHOTOS) ────────────────────────────────────────────── */
export function PhotoGridBlock({ photos, title = 'PHOTOS', phone, listed, onOpen }) {
  // A listed page's seeded photos are scraped and unvetted, so they never show until the business claims the page.
  if (listed || !photos.length) return <EmptyBlock title="PHOTOS" emptyTitle="NO PHOTOS" body={listed ? 'The business adds its work when it claims the page.' : 'No photos yet.'} phone={phone} testId="photos-block" />;
  return (
    <Block title={title} right={`${photos.length} PHOTO${photos.length === 1 ? '' : 'S'}`} phone={phone} testId="photos-block">
      <div className="grid" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${phone ? 90 : 140}px, 1fr))`, gap: 6 }}>
        {photos.map((p, k) => (
          <button key={p.id || k} type="button" onClick={() => onOpen?.(k)} className="block w-full overflow-hidden" style={{ aspectRatio: '1', borderRadius: 14, border: 'none', padding: 0, background: '#111', cursor: 'zoom-in' }} aria-label={`Photo ${k + 1}`}>
            <img src={p.url || p.src} alt={p.caption || ''} className="block h-full w-full object-cover" style={{ objectPosition: `${p.focalX ?? p.fx ?? 50}% ${p.focalY ?? p.fy ?? 50}%` }} />
          </button>
        ))}
      </div>
    </Block>
  );
}

/* ── Chips ─────────────────────────────────────────────────────────────────── */
export function ChipsBlock({ title, chips, phone, muted = false, testId }) {
  if (!chips?.length) return null;
  return (
    <Block title={title} phone={phone} testId={testId}>
      <span className="flex flex-wrap" style={{ gap: 6 }}>
        {chips.map((c) => <span key={c} className="flex items-center rounded-full" style={{ height: 34, padding: '0 14px', background: muted ? '#fff' : CHIP, color: muted ? MUTED : '#000', boxShadow: muted ? `inset 0 0 0 2px ${CHIP}` : 'none', ...head(10, '0.12em') }}>{c}</span>)}
      </span>
    </Block>
  );
}

/* ── EVENTS ────────────────────────────────────────────────────────────────── */
export function EventsBlock({ biz, phone, onEvent, hideEmpty = false }) {
  const listed = !!biz.isListed;
  const mine = !!biz.isMine && !listed;
  const all = biz.venueEvents || biz.events || [];
  const events = listed ? [] : all.filter((e) => new Date(e.dateTime) >= new Date(Date.now() - 6 * 3600 * 1000));
  if (events.length === 0) {
    if (hideEmpty && !mine) return null;
    if (listed) return <EmptyBlock title="EVENTS" emptyTitle="NO EVENTS" body="Events appear here once the business claims the page and puts them on it." phone={phone} testId="venue-events" bodyTestId="no-events" />;
    if (mine) return <EmptyBlock title="EVENTS" emptyTitle="NO EVENTS" body={all.length ? 'Nothing upcoming — past events stay linked in your dashboard.' : 'Nothing linked yet — add events from your dashboard.'} cta="ADD EVENTS →" ctaTo="/pro/dashboard?tab=page" ctaTestId="manage-events-link" phone={phone} testId="venue-events" bodyTestId="no-events" />;
    return <EmptyBlock title="EVENTS" emptyTitle="NO EVENTS" body="No events on this page yet." phone={phone} testId="venue-events" bodyTestId="no-events" />;
  }
  const pill = (status) => {
    const s = (status || '').toLowerCase();
    const funding = s === 'funding';
    const live = s === 'selling' || s === 'greenlit' || s === 'live';
    return <span className="rounded-full" style={{ padding: '3px 8px', background: funding ? '#FACC15' : live ? '#22C55E' : '#E5E7EB', color: funding || !live ? '#000' : '#fff', fontSize: 8, fontWeight: 800, letterSpacing: '0.14em' }}>{funding ? 'FUNDING' : live ? 'SELLING' : (status || 'DRAFT').toUpperCase()}</span>;
  };
  const thumb = phone ? 72 : 96;
  return (
    <Block title="EVENTS" right="ON THIS PAGE" phone={phone} testId="venue-events">
      <div className="flex flex-col" style={{ gap: 8 }}>
        {events.map((e) => (
          <button key={e.id} type="button" onClick={() => onEvent?.(e)} className="flex items-center text-left font-mono transition-colors hover:bg-[#EDEDED]" style={{ gap: 16, padding: '8px 16px 8px 8px', borderRadius: 22, background: '#F5F5F5', border: 'none', cursor: 'pointer', color: 'inherit' }} data-testid="venue-event">
            <span className="block flex-none overflow-hidden" style={{ width: thumb, height: thumb, borderRadius: 16, background: '#E5E7EB' }}>{e.posterUrl && <img src={e.posterUrl} alt="" className="block h-full w-full object-cover" />}</span>
            <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 6 }}>
              <span className="flex flex-wrap items-center" style={{ gap: 8 }}><span style={head(10, '0.14em')}>{dayMonthDay(e.dateTime)}</span>{pill(e.status)}</span>
              <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{e.title}</span>
              <span className="uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED }}>{[biz.doorsPolicy ? `DOORS ${biz.doorsPolicy}` : null, e.priceCents ? money(e.priceCents) : 'FREE', biz.ageMinimum ? `${biz.ageMinimum}+` : null, e.hostName ? `HOSTED BY ${e.hostName}` : null].filter(Boolean).join(' · ')}</span>
            </span>
            {!phone && <span className="flex flex-none items-center rounded-full" style={{ height: 38, padding: '0 16px', background: '#000', color: '#fff', ...head(10, '0.14em'), fontWeight: 800 }}>GET TICKETS</span>}
          </button>
        ))}
      </div>
    </Block>
  );
}

/* ── AVAILABILITY (7-day AM / PM) ──────────────────────────────────────────── */
export function WeekBlock({ biz, phone }) {
  const listed = !!biz.isListed;
  const weekly = biz.availability || [];
  const closed = (biz.availabilityDates || []).filter((d) => !d.am && !d.pm);
  const has = weekly.some((a) => a.am || a.pm) || closed.length > 0;
  if (listed) return <Block title="AVAILABILITY" phone={phone} testId="availability-block"><p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: MUTED }}>Not published. A listed business hasn’t set a calendar. Your request goes to them with your date on it.</p></Block>;
  if (!has) return <Block title="AVAILABILITY" phone={phone} testId="availability-block"><p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: MUTED }}>No calendar published. Send an offer with your date.</p></Block>;
  const zone = biz.timeZoneId || viewerZone();
  const cell = (on, k) => <span key={k} className="flex items-center justify-center" style={{ height: 30, borderRadius: 10, background: on ? HOT_PINK : '#F5F5F5', color: on ? '#fff' : '#C4C4C4', ...head(8, '0.1em') }}>{k}</span>;
  return (
    <Block title="AVAILABILITY" right={`TIMES IN ${zoneLabel(zone)}`} phone={phone} testId="availability-block">
      <div className="grid" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
        {[0, 1, 2, 3, 4, 5, 6].map((d) => {
          const a = weekly.find((x) => x.dayOfWeek === d) || {};
          return (
            <span key={d} className="flex flex-col" style={{ gap: 4 }}>
              <span className="text-center" style={{ ...head(9, '0.12em'), color: MUTED, paddingBottom: 2 }}>{DAYS[d]}</span>
              {cell(!!a.am, 'AM')}{cell(!!a.pm, 'PM')}
            </span>
          );
        })}
      </div>
      {closed.length > 0 && <span style={{ fontSize: 11, lineHeight: 1.6, color: MUTED }}>Closed {closed.slice(0, 6).map((d) => d.date).join(', ')}{closed.length > 6 ? ` and ${closed.length - 6} more` : ''}.</span>}
    </Block>
  );
}

/* ── FACES ─────────────────────────────────────────────────────────────────── */
export function FacesBlock({ biz, phone, onFace }) {
  const listed = !!biz.isListed;
  const team = biz.team || [];
  if (listed || team.length === 0) return <EmptyBlock title="FACES" emptyTitle="NO FACES YET" body={listed ? 'The business adds its people when it claims the page.' : 'Nobody has been added to this page.'} minH={100} phone={phone} testId="faces-block" />;
  return (
    <Block title="FACES" phone={phone} testId="faces-block">
      <div className="flex flex-wrap" style={{ gap: 16 }}>
        {team.map((t) => {
          const Face = t.linkedHandle ? 'button' : 'div';
          return (
            <Face key={t.id} {...(t.linkedHandle ? { type: 'button', onClick: () => onFace?.(t) } : {})} className={`flex flex-col items-center text-center font-mono ${t.linkedHandle ? 'hover:opacity-70' : ''}`} style={{ gap: 6, width: 76, background: 'none', border: 'none', padding: 0, cursor: t.linkedHandle ? 'pointer' : 'default', color: 'inherit' }}>
              <span className="flex items-center justify-center overflow-hidden rounded-full" style={{ width: 64, height: 64, background: '#F2F2F2', fontSize: 20, fontWeight: 700, color: FAINT }}>{t.avatarUrl ? <img src={t.avatarUrl} alt="" className="block h-full w-full object-cover" /> : (t.name || '?').charAt(0).toUpperCase()}</span>
              <span className="w-full truncate" style={{ fontSize: 11, fontWeight: 700 }}>{(t.name || '').split(' ')[0]}</span>
              {t.role && <span className="w-full truncate" style={{ fontSize: 10, color: MUTED }}>{t.role}</span>}
            </Face>
          );
        })}
      </div>
    </Block>
  );
}

/* ── Key / value ───────────────────────────────────────────────────────────── */
export function KvBlock({ title, right, rows, black = false, valueColor, keyColor, note, noteTag, noteTone, button, onButton, phone, testId }) {
  return (
    <Block title={title} right={right} black={black} phone={phone} testId={testId}>
      <div className="flex flex-col">
        {rows.map(([k, v], i) => (
          <span key={`${k}-${i}`} className="flex items-baseline justify-between" style={{ gap: 14, padding: '9px 0', borderTop: i === 0 ? 'none' : (black ? '1px solid #262626' : RULE) }}>
            <span className="uppercase" style={{ fontSize: 11, letterSpacing: '0.06em', color: keyColor || (black ? '#fff' : MUTED) }}>{k}</span>
            <span className="text-right" style={{ fontSize: 12, fontWeight: 700, color: valueColor || 'inherit' }}>{v}</span>
          </span>
        ))}
      </div>
      {note && (
        <span className="flex flex-wrap items-center" style={{ gap: '8px 10px', padding: '12px 14px', borderRadius: 18, background: '#F5F5F5' }}>
          <span className="rounded-full" style={{ padding: '3px 8px', background: noteTone, color: '#fff', fontSize: 8, fontWeight: 800, letterSpacing: '0.14em' }}>{noteTag}</span>
          <span style={{ flex: '1 1 160px', fontSize: 11, lineHeight: 1.55, color: '#374151' }}>{note}</span>
        </span>
      )}
      {button && <button type="button" onClick={onButton} className="font-mono transition-colors hover:bg-[#FF00AA]" style={{ height: 46, border: 'none', borderRadius: 999, background: '#000', color: '#fff', ...head(11, '0.14em'), fontWeight: 800, cursor: 'pointer' }}>{button}</button>}
    </Block>
  );
}

export function FlakeBlock({ biz, phone }) {
  if (biz.isListed || !(biz.flakeTiers?.length > 0)) return null;
  return <KvBlock title="IF PLANS CHANGE" rows={biz.flakeTiers.map((t) => [`Cancel ${t.comparator === 'more' ? '≥' : 'under'} ${t.daysThreshold} days out`, `keeps ${t.feePercent}%`])} valueColor={HOT_PINK} phone={phone} testId="flake-block" />;
}

export function HappyHourBlock({ biz, phone }) {
  if (!(biz.happyHours?.length > 0)) return null;
  return <KvBlock title="HAPPY HOUR" black rows={biz.happyHours.map((h) => [`${daysLabel(h.days)} · ${clock12(h.from)} – ${clock12(h.to)}`, h.note || ''])} valueColor="#FF66C4" phone={phone} testId="happy-hour" />;
}

export function WhatsOnBlock({ biz, phone }) {
  if (!(biz.recurringNights?.length > 0)) return null;
  return <KvBlock title="WHAT’S ON" right="EVERY WEEK" keyColor="#000" rows={biz.recurringNights.map((n) => [n.title, [weeklyLabel(n.days), clock12(n.time), n.note ? n.note.toUpperCase() : null].filter(Boolean).join(' · ')])} valueColor={MUTED} phone={phone} testId="whats-on" />;
}

export function HireBlock({ biz, phone, onHire }) {
  const listed = !!biz.isListed;
  const rows = listed
    ? [['CAPACITY', biz.capacity ? `≈${biz.capacity} (POSTED)` : 'NOT POSTED'], ['HIRE RATE', biz.hireRateCents ? `${money(biz.hireRateCents)} (POSTED)` : 'NOT POSTED'], ['CONTACT', biz.phone ? 'PHONE ON SITE' : biz.websiteUrl || biz.listedWebsite ? 'ON THEIR SITE' : 'NOT POSTED']]
    : [biz.capacity ? ['CAPACITY', `${biz.capacity} CAP`] : null, biz.hireRateCents ? ['PRIVATE HIRE', `${money(biz.hireRateCents)} / NIGHT`] : null, biz.hireIncludes ? ['INCLUDED', biz.hireIncludes.toUpperCase()] : null, (biz.ageMinimum || biz.doorsPolicy) ? ['AGE · DOORS', [biz.ageMinimum ? `${biz.ageMinimum}+` : null, biz.doorsPolicy].filter(Boolean).join(' · ')] : null, biz.coverRange ? ['COVER', biz.coverRange.toUpperCase()] : null, biz.dressCode ? ['DRESS', biz.dressCode] : null, biz.soundSpec ? ['SOUND AND STAGE', biz.soundSpec] : null].filter(Boolean);
  return (
    <KvBlock title="HIRE THIS ROOM" right={listed ? 'AS POSTED' : 'FOR HOSTS AND THE PLANNER'} rows={rows.length ? rows : [['ROOM', 'Capacity and rate not stated yet']]} valueColor={listed ? MUTED : '#000'}
      noteTag={listed ? 'FOUND ON THEIR SITE' : 'STATED BY THE VENUE'} noteTone={listed ? FAINT : '#16A34A'} note={listed ? 'The planner treats this as a guess until someone claims it.' : `${biz.claimedAt ? `Claimed ${dayMonthDay(biz.claimedAt)}. ` : ''}The planner takes this over a web guess.`}
      button={!listed && !biz.isMine ? 'ASK ABOUT A DATE' : null} onButton={onHire} phone={phone} testId="hire-block" />
  );
}

export function CertsBlock({ biz, phone }) {
  if (!biz.certifications && !biz.warrantyLine) return null;
  return <Block title="CERTIFICATIONS" phone={phone} testId="certs-block"><p style={{ margin: 0, fontSize: 13, lineHeight: 1.7 }}>{[biz.certifications, biz.warrantyLine].filter(Boolean).join(' ')}</p></Block>;
}

export function ReviewsBlock({ biz, phone }) {
  if (biz.isListed || !(biz.testimonials?.length > 0)) return null;
  return (
    <Block title="VERIFIED REVIEWS" right="ESCROW-VERIFIED" phone={phone} testId="reviews-block">
      <div className="flex flex-col" style={{ gap: 12 }}>
        {biz.testimonials.map((t) => {
          const r = Math.max(1, Math.min(5, t.rating));
          return (
            <div key={t.id} className="flex flex-col" style={{ gap: 4, padding: '12px 14px', borderRadius: 18, background: '#F5F5F5' }}>
              <span style={{ fontSize: 13, color: HOT_PINK }}>{'★'.repeat(r)}<span style={{ color: '#D1D5DB' }}>{'★'.repeat(5 - r)}</span></span>
              {t.text && <p style={{ margin: 0, fontFamily: '"Times New Roman", Times, serif', fontStyle: 'italic', fontSize: 15, lineHeight: 1.45, color: '#374151' }}>“{t.text}”</p>}
              {t.authorName && <span style={{ ...head(9, '0.14em'), color: MUTED }}>— {t.authorName}</span>}
            </div>
          );
        })}
      </div>
    </Block>
  );
}

/* ── HOURS · WHERE · ATTRIBUTES ─────────────────────────────────────────────── */
export function HoursBlock({ biz, phone }) {
  const listed = !!biz.isListed;
  const hours = biz.hours || [];
  if (hours.length === 0) return null;
  const state = listed ? null : openState(hours);
  const today = new Date().getDay();
  const pill = listed ? { label: 'AS POSTED', bg: CHIP, fg: MUTED, testId: 'hours-pill' }
    : state ? { label: state.open ? state.label : (state.label.startsWith('CLOSED') ? state.label : 'CLOSED'), bg: state.open ? '#16A34A' : '#E5E7EB', fg: state.open ? '#fff' : '#000', testId: 'hours-pill' }
      : { label: 'HOURS NOT SET', bg: CHIP, fg: MUTED, testId: 'hours-pill' };
  const kitchen = hours.find((h) => h.kitchenCloses && !h.isClosed);
  return (
    <Block title="HOURS" rightPill={pill} phone={phone} testId="hours-card">
      <div className="flex flex-col" style={{ gap: 2 }}>
        {DAY_ORDER.map((d) => {
          const h = hours.find((x) => x.dayOfWeek === d);
          const isToday = !listed && d === today;
          const text = hoursRowText(h);
          const closed = /closed/i.test(text);
          return (
            <span key={d} className="flex justify-between" style={{ gap: 12, padding: '8px 12px', borderRadius: 12, background: isToday ? '#F5F5F5' : 'transparent', fontSize: 12 }}>
              <span className="uppercase" style={{ fontWeight: isToday ? 800 : 400, letterSpacing: '0.08em', color: isToday ? '#000' : MUTED }}>{DAYS[d]}</span>
              <span style={{ fontWeight: isToday ? 800 : 400, color: closed ? FAINT : '#000' }}>{closed ? 'Closed' : text}</span>
            </span>
          );
        })}
      </div>
      {kitchen && <span style={{ fontSize: 11, color: MUTED }}>Kitchen closes {clock12(kitchen.kitchenCloses)}.</span>}
    </Block>
  );
}

export function WhereBlock({ biz, phone, onOutbound }) {
  const listed = !!biz.isListed;
  const city = biz.baseCity || biz.listedCity;
  if (!(biz.streetAddress || biz.neighbourhood || biz.parkingNote || city)) return null;
  const mapsHref = biz.streetAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${biz.streetAddress}${city ? `, ${city}` : ''}`)}` : null;
  return (
    <Block title="WHERE" phone={phone} testId="where-card">
      <span className="flex flex-col" style={{ gap: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4 }}>{[biz.streetAddress, city].filter(Boolean).join(', ')}</span>
        {biz.neighbourhood && <span style={{ ...head(10, '0.14em'), color: MUTED }}>{biz.neighbourhood}</span>}
        {biz.parkingNote && <span style={{ fontSize: 12, lineHeight: 1.5, color: MUTED }}>{biz.parkingNote}</span>}
      </span>
      <div data-testid="where-map"><AddressMap biz={biz} href={listed ? null : mapsHref} width={640} height={170} fluid radius={18} /></div>
      {mapsHref && !listed && <ActionPill href={mapsHref} onClick={() => onOutbound?.('directions')} testId="directions">DIRECTIONS ↗</ActionPill>}
    </Block>
  );
}

export function AttributesBlock({ biz, phone }) {
  const attrs = (biz.attributes || []).map(attrLabel);
  return <ChipsBlock title="ATTRIBUTES" chips={attrs} muted={!!biz.isListed} phone={phone} testId="attributes-card" />;
}

export function BrandsBlock({ biz, phone }) {
  const brands = (biz.brands || '').split(',').map((b) => b.trim()).filter(Boolean).map((b) => b.toUpperCase());
  return <ChipsBlock title="BRANDS" chips={brands} phone={phone} testId="brands-block" />;
}

/* ── SHARE ─────────────────────────────────────────────────────────────────── */
export function ShareBlock({ url, listed, phone }) {
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);
  useEffect(() => { if (!copied) return undefined; const t = setTimeout(() => setCopied(false), 1600); return () => clearTimeout(t); }, [copied]);
  const copy = async () => { try { await navigator.clipboard?.writeText(url); } catch { /* the URL is on screen */ } setCopied(true); };
  return (
    <Block title={listed ? 'SHARE THIS LISTING' : 'SHARE THIS PRO'} phone={phone} testId="share-block">
      <span className="truncate" style={{ fontSize: 12, color: MUTED }}>{url.replace(/^https?:\/\//, '')}</span>
      <span className="flex" style={{ gap: 6 }}>
        <button type="button" onClick={copy} className="font-mono" style={{ flex: 1, height: 40, border: 'none', borderRadius: 999, background: copied ? '#00FFFF' : '#000', color: copied ? '#000' : '#fff', ...head(10, '0.14em'), fontWeight: 800, cursor: 'pointer', transition: 'background-color .2s' }} data-testid="copy-link">{copied ? 'COPIED' : 'COPY LINK'}</button>
        <button type="button" onClick={() => setQr((q) => !q)} aria-pressed={qr} className="font-mono" style={{ flex: 1, height: 40, border: 'none', borderRadius: 999, background: CHIP, ...head(10, '0.14em'), cursor: 'pointer' }} data-testid="qr-toggle">QR CODE</button>
      </span>
      {qr && <span className="flex justify-center" style={{ padding: 12, borderRadius: 18, background: '#F5F5F5' }}><BrandedQR value={url} size={160} /></span>}
    </Block>
  );
}

/* ── MENU (list · plate · spread) ──────────────────────────────────────────── */
function Marks({ marks, size = 22, bg = CHIP }) {
  if (!marks?.length) return null;
  return <span className="flex flex-none" style={{ gap: 3 }}>{marks.map((m) => <span key={m} className="flex items-center justify-center" style={{ width: size, height: size, borderRadius: size > 20 ? 7 : 6, background: bg, fontSize: 8, fontWeight: 800 }}>{m}</span>)}</span>;
}

function MenuRows({ items, showAbv, spread = false, phone }) {
  return (
    <div className="flex flex-col" style={{ padding: '0 4px' }}>
      {items.map((it, i) => (
        <div key={it.id || i} className="flex items-baseline" style={{ gap: 14, padding: spread && !phone ? '16px 0' : '11px 0', borderTop: i === 0 ? 'none' : RULE }} data-testid="menu-row">
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
            <span style={{ fontSize: spread && !phone ? 15 : 14, lineHeight: 1.3 }}>{it.name}</span>
            {(it.description || it.style) && <span style={{ fontSize: 11, lineHeight: 1.45, color: MUTED }}>{it.description || it.style}</span>}
          </span>
          {showAbv && <span className="flex-none text-right" style={{ width: 52, fontSize: 11, fontWeight: 700, color: MUTED }}>{it.abv != null ? `${it.abv}%` : '—'}</span>}
          <Marks marks={it.marks} />
          <span className="flex-none text-right" style={{ minWidth: 64, fontSize: spread && !phone ? 15 : 14, fontWeight: 800 }}>{priceLabel(it) || '—'}</span>
        </div>
      ))}
    </div>
  );
}

export function SoftMenu({ sections, listed = false, showAbv = false, legend = true, phone }) {
  const visible = (sections || []).filter((s) => s.items?.length > 0);
  if (visible.length === 0) return null;
  const anyMarks = visible.some((s) => s.items.some((i) => i.marks?.length));
  return (
    <div className="flex flex-col" style={{ gap: 18 }} data-testid="full-menu">
      {visible.map((s, si) => {
        const layout = listed ? 'list' : (s.effectiveLayout || 'list');
        const abv = showAbv && !!s.showAbv;
        return (
          <div key={s.id || si} className="flex flex-col" style={{ gap: 10 }} data-layout={layout}>
            <span className="flex flex-wrap items-baseline justify-between" style={{ gap: '4px 12px', padding: '11px 16px', borderRadius: 14, background: '#000', color: '#fff' }}>
              <span style={head(11, '0.14em')}>{s.name}</span>
              {s.note && <span style={{ fontSize: 10, color: FAINT }}>{s.note}</span>}
            </span>
            {layout === 'plate' ? (
              <div className="grid" style={{ gridTemplateColumns: `repeat(${phone ? 2 : 4}, minmax(0, 1fr))`, gap: 10 }}>
                {s.items.map((it, i) => (
                  <div key={it.id || i} className="flex min-w-0 flex-col overflow-hidden" style={{ borderRadius: 18, background: '#F5F5F5' }} data-testid="plate-card">
                    <span className="block w-full" style={{ aspectRatio: '1', background: '#E5E7EB' }}>{it.photoUrl && <img src={it.photoUrl} alt={it.name} className="block h-full w-full object-cover" />}</span>
                    <span className="flex flex-1 flex-col" style={{ gap: 5, padding: '11px 12px 6px' }}><span style={{ fontSize: 13, lineHeight: 1.3 }}>{it.name}</span>{!phone && it.description && <span style={{ fontSize: 11, lineHeight: 1.45, color: MUTED }}>{it.description}</span>}</span>
                    <span className="flex items-center justify-between" style={{ gap: 6, padding: '6px 12px 12px' }}><Marks marks={it.marks} size={20} bg="#fff" /><span style={{ fontSize: 13, fontWeight: 800 }}>{priceLabel(it)}</span></span>
                  </div>
                ))}
              </div>
            ) : layout === 'spread' ? (
              <div className="flex items-stretch" style={{ flexDirection: phone ? 'column' : (si % 2 ? 'row-reverse' : 'row'), gap: 14 }}>
                <span className="block overflow-hidden" style={{ flex: phone ? '0 0 auto' : '0 0 40%', height: phone ? 176 : 260, borderRadius: 18, background: '#E5E7EB' }}>{s.photoUrl && <img src={s.photoUrl} alt={s.name} className="block h-full w-full object-cover" />}</span>
                <div className="flex min-w-0 flex-1 flex-col justify-center"><MenuRows items={s.items} showAbv={abv} spread phone={phone} /></div>
              </div>
            ) : <MenuRows items={s.items} showAbv={abv} phone={phone} />}
          </div>
        );
      })}
      {legend && anyMarks && (
        <span className="flex flex-wrap items-center" style={{ gap: '6px 10px', padding: '10px 16px', borderRadius: 14, background: '#F5F5F5' }}>
          <span style={{ ...head(9, '0.14em'), color: MUTED }}>MARKS</span>
          <span style={{ fontSize: 11, color: MUTED }}>V vegetarian · VG vegan · GF gluten free · N contains nuts</span>
        </span>
      )}
    </div>
  );
}

export function MenuBlock({ biz, phone, onOutbound }) {
  const listed = !!biz.isListed;
  const sections = biz.menuSections || [];
  if (listed || sections.every((s) => !(s.items?.length))) {
    const site = biz.websiteUrl || biz.listedWebsite;
    return <EmptyBlock title="MENU" emptyTitle="NO MENU HERE" body={listed ? 'We have a link but no items. The kitchen adds them when it claims the page.' : 'Nothing on the menu yet.'} cta={site ? 'MENU ON THEIR SITE →' : null} onCta={() => { onOutbound?.('website'); window.open(site, '_blank', 'noopener'); }} ctaTestId="menu-on-site" phone={phone} testId="menu-card" bodyTestId="no-menu-here" />;
  }
  return <Block title="MENU" right={phone ? undefined : 'PRICES SET BY THE KITCHEN · NOT BOOKABLE HERE'} phone={phone} testId="menu-card"><SoftMenu sections={sections} phone={phone} /></Block>;
}

export const splitBarSections = (sections) => {
  const snacks = (sections || []).filter((s) => /snack|food|bites|kitchen/i.test(s.name || ''));
  return { snacks, drinks: (sections || []).filter((s) => !snacks.includes(s)) };
};

export function DrinksBlock({ biz, phone }) {
  const listed = !!biz.isListed;
  const { drinks } = splitBarSections(biz.menuSections);
  const has = drinks.some((s) => s.items?.length);
  return (
    <Block title="DRINKS" right={listed ? 'AS POSTED' : 'ON TAP TODAY'} phone={phone} testId="drinks-block">
      {!has && <span style={{ fontSize: 12, color: MUTED }}>{listed ? 'Nothing posted. Ask them what’s on.' : 'Nothing on the list yet.'}</span>}
      <SoftMenu sections={drinks} listed={listed} showAbv legend={false} phone={phone} />
    </Block>
  );
}

/* ── OFFERS ────────────────────────────────────────────────────────────────── */
const valueLine = (o) => (o.valueText ? o.valueText.toUpperCase() : o.valueCents ? `${money(o.valueCents)} VALUE` : null);
const stockLine = (o) => (o.remaining == null ? 'NO LIMIT' : `${o.remaining} LEFT`);

export function OfferCard({ offer, onClaim, onRelease, busy = false, canClaimHint, phone }) {
  const claim = offer.viewerClaim;
  if (claim && claim.state === 'active') {
    const url = `${webOrigin()}/offer/${claim.id}`;
    return (
      <div className="flex flex-col font-mono" style={{ gap: 12, padding: 16, borderRadius: 20, background: '#000', color: '#fff' }} data-testid="offer-card-claimed">
        <span style={{ ...head(9, '0.14em'), color: '#22C55E' }}>CLAIMED · IN YOUR WALLET</span>
        <span style={{ fontSize: 16, lineHeight: 1.3 }}>{offer.title}</span>
        <span style={{ fontSize: 11, lineHeight: 1.55, color: FAINT }}>Show this at the table. It comes off the bill there. Nothing is charged through Portal.</span>
        <span className="flex flex-col items-center" style={{ gap: 10, padding: 16, borderRadius: 16, background: '#161616' }}>
          <span style={{ ...head(9, '0.14em'), color: FAINT }}>SHOW THIS CODE</span>
          <span className="rounded-[12px] bg-white" style={{ padding: 6 }}><BrandedQR value={url} size={108} /></span>
          <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: '0.14em' }} data-testid="claim-code">{claim.code}</span>
        </span>
        <span className="flex items-center justify-between" style={{ gap: 10 }}>
          <span style={{ ...head(9, '0.14em'), color: FAINT }}>{offer.expiresAt ? `EXPIRES ${monthDay(offer.expiresAt)}` : 'NO EXPIRY'}</span>
          <button type="button" onClick={() => onRelease?.(claim)} disabled={busy} className="font-mono disabled:opacity-50" style={{ height: 36, padding: '0 14px', border: 'none', borderRadius: 999, background: '#262626', color: '#fff', ...head(10, '0.14em'), cursor: 'pointer' }}>GIVE IT BACK</button>
        </span>
      </div>
    );
  }
  const ended = offer.phase === 'ran_out' || offer.phase === 'ended';
  return (
    <div className="flex flex-col overflow-hidden font-mono" style={{ borderRadius: 20, background: '#F5F5F5', opacity: ended ? 0.55 : 1 }} data-testid="offer-card">
      {offer.photoUrl && !phone && <img src={offer.photoUrl} alt="" className="block w-full object-cover" style={{ height: 168 }} />}
      <span className="flex flex-col" style={{ gap: 8, padding: '16px 16px 10px' }}>
        <span style={{ fontSize: 16, lineHeight: 1.3 }}>{offer.title}</span>
        <span className="flex flex-wrap items-baseline" style={{ gap: '4px 10px' }}>
          {valueLine(offer) && <span style={{ fontSize: 12, fontWeight: 800 }}>{valueLine(offer)}</span>}
          {offer.expiresAt && <span className="uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED }}>{ended ? 'ENDED' : 'UNTIL'} {monthDay(offer.expiresAt)}</span>}
        </span>
        {offer.terms && <span style={{ fontSize: 12, lineHeight: 1.55, color: MUTED }}>{offer.terms}</span>}
      </span>
      <span className="flex items-center justify-between" style={{ gap: 10, padding: '8px 12px 12px 16px' }}>
        <span style={{ ...head(9, '0.14em'), color: FAINT }}>{ended ? (offer.phase === 'ran_out' ? 'RAN OUT' : 'ENDED') : stockLine(offer)}</span>
        {!ended && (
          <button type="button" onClick={() => onClaim?.(offer)} disabled={busy || (!offer.canClaim && !canClaimHint)} title={!offer.canClaim && canClaimHint ? canClaimHint : undefined}
            className="font-mono transition-colors hover:bg-[#FF00AA] disabled:opacity-50" style={{ height: 40, padding: '0 20px', border: 'none', borderRadius: 999, background: '#000', color: '#fff', ...head(10, '0.14em'), fontWeight: 800, cursor: 'pointer' }}>
            {claim && claim.state === 'used' && offer.usageMode === 'one_per_guest' ? 'USED' : 'CLAIM'}
          </button>
        )}
      </span>
    </div>
  );
}

export function OffersBlock({ biz, offers, phone, onClaim, onRelease, busyId, canClaimHint }) {
  const listed = !!biz.isListed;
  // Every kind has OFFERS, so the copy can't say "at the table" — a bookable pro shows the
  // claim when they book, a place shows it on the way in.
  const bookable = !biz.kind || biz.kind === 'bookable';
  const title = `OFFERS AT ${(biz.name || '').toUpperCase()}`;
  if (listed || offers.length === 0) return <EmptyBlock title={title} emptyTitle="NO OFFERS RIGHT NOW" body={listed ? 'Offers appear when the business claims the page.' : `Check back. Offers posted here are yours to claim and ${bookable ? 'use when you book' : 'carry in'}.`} phone={phone} testId="offers-block" />;
  const claimed = (o) => o.viewerClaim?.state === 'active';
  const sorted = phone ? [...offers.filter((o) => !claimed(o)), ...offers.filter(claimed)] : offers;
  return (
    <Block title={title} right={phone ? undefined : `CLAIM IT HERE · SHOW IT ${bookable ? 'WHEN YOU BOOK' : 'AT THE TABLE'}`} phone={phone} testId="offers-block">
      <div className="grid items-start" style={{ gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
        {sorted.map((o) => <OfferCard key={o.id} offer={o} onClaim={onClaim} onRelease={onRelease} busy={busyId === o.id} canClaimHint={canClaimHint} phone={phone} />)}
      </div>
    </Block>
  );
}

export function OfferStrip({ count, onSee, phone }) {
  if (!count) return null;
  return (
    <Block black phone={phone} testId="offers-strip">
      <span className="flex items-center justify-between" style={{ gap: 10 }}>
        <span className="flex flex-col" style={{ gap: 3 }}>
          <span style={head(11, '0.12em')}>{count} OFFER{count === 1 ? '' : 'S'} HERE</span>
          <span style={{ fontSize: 10, color: FAINT }}>Claim one before you go.</span>
        </span>
        <button type="button" onClick={onSee} className="font-mono" style={{ flexShrink: 0, height: 36, padding: '0 16px', border: 'none', borderRadius: 999, background: '#fff', color: '#000', ...head(10, '0.14em'), fontWeight: 800, cursor: 'pointer' }}>SEE THEM</button>
      </span>
    </Block>
  );
}

/* ── Listed footers ────────────────────────────────────────────────────────── */
export function AsPostedBlock({ phone }) {
  return (
    <Block phone={phone} testId="as-posted-note">
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.7, color: MUTED }}>Everything on this page is <b style={{ color: '#000' }}>AS POSTED</b>, read off the business’s own site and marked so. Hours carry the label instead of OPEN NOW, because we can’t promise a clock we didn’t set.</p>
    </Block>
  );
}
